import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Flame, Zap, Target, MessageSquareText, Puzzle, ShieldAlert,
    Sparkles, GraduationCap, CalendarClock, Users, Lock, BarChart3, FileUp,
    Check, Mail, Brain, Compass, Scale, Infinity as InfinityIcon,
} from "lucide-react";

const Feature = ({ icon: Icon, title, body, testid }) => (
    <div
        data-testid={testid}
        className="border border-zinc-900 p-7 bg-[#0c0c0c] hover:border-signal/50 transition-colors group"
    >
        <Icon className="w-6 h-6 text-signal mb-4" strokeWidth={1.5} />
        <h3 className="font-heading font-bold text-xl mb-2">{title}</h3>
        <p className="text-zinc-400 text-sm leading-relaxed">{body}</p>
    </div>
);

const MethodologyCard = ({ id, name, who, line, when }) => (
    <div
        data-testid={`methodology-${id}`}
        className="border border-zinc-900 bg-[#0c0c0c] p-6 hover:border-signal/50 transition-colors"
    >
        <div className="flex items-start justify-between mb-2">
            <h4 className="font-heading font-bold text-lg">{name}</h4>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">{who}</span>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed mb-3">{line}</p>
        <div className="text-xs font-mono uppercase tracking-widest text-signal">Best for: <span className="text-zinc-400 normal-case tracking-normal">{when}</span></div>
    </div>
);

const PLANS = [
    {
        id: "free",
        name: "Free",
        price: "$0",
        unit: "/mo",
        tagline: "Try it. No card required.",
        highlight: false,
        ctaLabel: "Start free",
        ctaTo: "/signup",
        cap: "500 generations / month",
        users: "1 user",
        features: [
            "500 AI generations / month",
            "Cold Outreach Generator",
            "Coach Mode (NEPQ / Jeremy Miner)",
            "Full 60-day follow-up library",
            "32+ objection templates",
            "Chrome extension",
        ],
        excluded: ["Team seats", "Unlimited generations", "Admin console", "Priority support"],
    },
    {
        id: "standard",
        name: "Standard",
        price: "$19",
        unit: "/mo",
        tagline: "For the solo closer.",
        highlight: false,
        ctaLabel: "Subscribe to Standard",
        ctaTo: "/signup",
        cap: "5,000 generations / month",
        users: "1 user",
        features: [
            "5,000 AI generations / month",
            "Everything in Free",
            "Document uploads (PDF · DOCX · TXT) for Outreach",
            "All 8 sales methodologies (NEPQ, SPIN, Challenger, Sandler, MEDDIC, Gap, Consultative, Straight Line)",
            "Full objection + follow-up library",
            "Email support",
        ],
        excluded: ["Team seats", "Admin console"],
    },
    {
        id: "pro",
        name: "Pro",
        price: "$49",
        unit: "/mo",
        tagline: "For teams closing together.",
        highlight: true,
        ctaLabel: "Subscribe to Pro",
        ctaTo: "/signup",
        cap: "Unlimited generations",
        users: "10 users included",
        features: [
            "Unlimited AI generations",
            "Up to 10 team members included",
            "Add extra users at $8/mo each",
            "Shared team workspace (history, templates)",
            "Team Admin console",
            "Usage analytics",
            "All 8 sales methodologies",
            "Priority chat support",
        ],
        excluded: ["SSO", "Dedicated CSM"],
        footnote: "Extra users: $8 / user / month",
    },
    {
        id: "enterprise",
        name: "Enterprise",
        price: "Custom",
        unit: "",
        tagline: "For the org that owns the pipeline.",
        highlight: false,
        ctaLabel: "Contact Sales",
        ctaTo: "mailto:sales@saleswhisper.dev?subject=Enterprise%20plan%20enquiry",
        external: true,
        cap: "Unlimited generations",
        users: "Unlimited users",
        features: [
            "Everything in Pro",
            "Unlimited team seats",
            "SSO (Google / Microsoft / Okta)",
            "Dedicated Customer Success Manager",
            "Custom onboarding + methodology tuning",
            "Private LLM routing available",
            "SOC 2 review + custom DPA",
            "99.9% uptime SLA",
        ],
        excluded: [],
    },
];

const METHODOLOGIES = [
    { id: "nepq", name: "NEPQ", who: "Jeremy Miner", line: "Diagnostic questions that make the prospect sell themselves on the problem before you ever pitch.", when: "Cold DMs · discovery · breaking status-quo bias" },
    { id: "spin", name: "SPIN", who: "Neil Rackham", line: "Situation → Problem → Implication → Need-payoff. Walks the prospect from symptom to self-aware pain.", when: "Complex B2B · long cycles · multi-stakeholder" },
    { id: "challenger", name: "Challenger", who: "Dixon & Adamson", line: "Teach · Tailor · Take control. Reframe the prospect's world before they know they need reframing.", when: "Commoditised markets · jaded buyers · incumbent threat" },
    { id: "sandler", name: "Sandler", who: "David Sandler", line: "Up-front contracts, pain-based qualification, disqualify early. Reverse psychology over persuasion.", when: "High-ticket · gatekeeping · avoiding the 'think-it-over'" },
    { id: "meddic", name: "MEDDIC", who: "Dick Dunkel", line: "Metrics, Economic Buyer, Decision Criteria, Decision Process, Identify Pain, Champion. Rigor over rapport.", when: "Enterprise SaaS · forecasting accuracy · procurement" },
    { id: "gap", name: "Gap Selling", who: "Keenan", line: "Quantify the dollar gap between current state and desired state. Without a gap, there's no deal.", when: "ROI-sensitive buyers · CFO involvement · budget stalls" },
    { id: "consultative", name: "Consultative", who: "Mack Hanan", line: "Advisor mindset. Ask before telling. Prescribe a solution only after deep understanding.", when: "Relationship businesses · referrals · founder-led sales" },
    { id: "straight_line", name: "Straight Line", who: "Jordan Belfort", line: "High-certainty tonality, tight logical rails, anticipate and dissolve objections on the way.", when: "Inside sales · scripted calls · (use with care — risk of sounding salesy)" },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white relative overflow-hidden">
            {/* Nav */}
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a0a0a]/80 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
                        <Flame className="w-5 h-5 text-signal" />
                        <span className="font-heading font-black text-lg tracking-tight">
                            sales<span className="text-signal">whisper</span>
                        </span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
                        <a href="#features" className="hover:text-white transition-colors">Features</a>
                        <a href="#methodologies" className="hover:text-white transition-colors">Methodologies</a>
                        <a href="#how" className="hover:text-white transition-colors">How it works</a>
                        <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
                        <a href="#extension" className="hover:text-white transition-colors">Extension</a>
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link to="/login" className="text-sm text-zinc-300 hover:text-white px-3 py-2" data-testid="landing-login-btn">
                            Sign in
                        </Link>
                        <Link
                            to="/signup"
                            data-testid="landing-cta-signup"
                            className="bg-signal hover:bg-signal-hover text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
                        >
                            Start free
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                {/* Looping background video (muted, autoplay) */}
                <video
                    data-testid="hero-video"
                    src="/media/hero.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                />
                {/* Readability overlays — keep all hero text crisp on top of the video */}
                <div className="absolute inset-0 bg-[#0a0a0a]/55 pointer-events-none" aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/85 to-[#0a0a0a]/30 pointer-events-none" aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0a0a] pointer-events-none" aria-hidden="true" />
                {/* Existing orange glow on top of overlays for the signature look */}
                <div className="absolute inset-0 orange-glow pointer-events-none" aria-hidden="true" />

                <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-20 pb-24 md:pt-28 md:pb-32 relative">
                    <div className="max-w-3xl fade-up">
                        <div className="inline-flex items-center gap-2 px-3 py-1 border border-zinc-800 rounded-full text-xs text-zinc-400 mb-8 font-mono uppercase tracking-widest">
                            <span className="w-1.5 h-1.5 bg-signal rounded-full animate-pulse" />
                            Built for appointment setters
                        </div>
                        <h1 className="font-heading font-black text-5xl sm:text-6xl md:text-7xl tracking-tighter leading-[0.95] mb-6">
                            Close the DM.
                            <br />
                            <span className="text-signal">Not the prospect.</span>
                        </h1>
                        <p className="text-lg md:text-xl text-zinc-300 max-w-2xl mb-10 leading-relaxed">
                            The AI co-pilot trained on the 8 proven sales methodologies — NEPQ, SPIN, Challenger, Sandler, MEDDIC, Gap Selling, Consultative, and Straight Line. Write LinkedIn & Instagram replies like a top-1% human closer. Never salesy. Never cringe.
                        </p>
                        <div className="flex flex-wrap gap-3 items-center">
                            <Link
                                to="/signup"
                                data-testid="hero-cta-signup"
                                className="bg-signal hover:bg-signal-hover text-white px-6 py-3.5 rounded-md font-semibold flex items-center gap-2 transition-colors group"
                            >
                                Start free — 500 generations
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <a
                                href="#pricing"
                                data-testid="hero-cta-pricing"
                                className="border border-zinc-800 hover:border-zinc-600 text-white px-6 py-3.5 rounded-md font-semibold flex items-center gap-2 transition-colors"
                            >
                                See pricing
                            </a>
                        </div>
                        <div className="mt-14 flex flex-wrap items-center gap-4 md:gap-6 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                            <span>Powered by Claude Sonnet 4.5</span>
                            <span className="hidden sm:block w-8 h-px bg-zinc-800" />
                            <span>8 methodologies · 11 industries · 5 tones</span>
                            <span className="hidden sm:block w-8 h-px bg-zinc-800" />
                            <span className="hidden md:block">Chrome extension · Team workspaces</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core features — expanded bento */}
            <section id="features" className="border-t border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="mb-14 max-w-3xl">
                        <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-4">/ 01 — FEATURES</div>
                        <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight leading-tight mb-5">
                            Every feature built around <span className="text-signal">one job</span>: book the meeting.
                        </h2>
                        <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
                            This isn't a prompt library. It's an end-to-end setter's workflow — from the first cold DM all the way through a 60-day follow-up cadence, with a coach that grades your conversations in between.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <Feature
                            icon={Target}
                            title="Cold Outreach Generator"
                            body="Tell us what you're selling + who you're chasing. Pick a methodology. Upload a brochure (PDF/DOCX/TXT). Get a first message, two alt openers, and day-4 + interested-reply follow-ups — all under 60 words each, diagnostic-first, never pitchy."
                            testid="feature-outreach"
                        />
                        <Feature
                            icon={MessageSquareText}
                            title="Reply Engine · 3 Variants"
                            body="Paste a live LinkedIn or Instagram thread. Pick tone + industry. Get value-first, curiosity-hook, and direct-ask variants with confidence scores and the exact reasoning behind each angle."
                            testid="feature-variants"
                        />
                        <Feature
                            icon={GraduationCap}
                            title="Coach Mode"
                            body="Paste any conversation and get graded A–F on status, framing, and question quality. Learn what you missed: diagnostic NEPQ questions, labels, mirrors, take-aways — plus a rewrite of your last message."
                            testid="feature-coach"
                        />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <Feature
                            icon={CalendarClock}
                            title="60-Day Follow-up Cadence"
                            body="19 battle-tested touchpoints mapped Day 0 → Day 60: soft bumps, Loom walkthroughs, case studies, pattern interrupts, scarcity frames, and graceful breakups. Copy, send, move on."
                            testid="feature-followups"
                        />
                        <Feature
                            icon={ShieldAlert}
                            title="Objection Vault"
                            body="32+ pre-built frameworks across 12 categories — price, time, competitor, trust, authority, need, fit, ghosted, risk, and vertical-specific (insurance, medical, home services)."
                            testid="feature-objections"
                        />
                        <Feature
                            icon={FileUp}
                            title="Document Intelligence"
                            body="Drop your product sheet, spec doc, or case study (PDF · DOCX · TXT · up to 10MB). We extract the text and feed it as context — so the AI writes about your product, not generic SaaS."
                            testid="feature-docs"
                        />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <Feature
                            icon={Puzzle}
                            title="Chrome Extension"
                            body="A Manifest v3 extension that lives on LinkedIn + Instagram. One click pops open the composer with your chosen tone + industry pre-selected. Download as a zip from inside the app."
                            testid="feature-extension"
                        />
                        <Feature
                            icon={Users}
                            title="Team Workspaces"
                            body="Invite teammates (up to 10 on Pro, unlimited on Enterprise). Share history, templates, and the objection vault across the team. Owners manage seats and billing."
                            testid="feature-team"
                        />
                        <Feature
                            icon={BarChart3}
                            title="Admin Console"
                            body="One dashboard for team owners: user management, seat provisioning, usage stats, revenue, and plug-in-your-own-Stripe-key support for agencies reselling the service."
                            testid="feature-admin"
                        />
                    </div>
                </div>
            </section>

            {/* Sales Methodologies — the differentiator */}
            <section id="methodologies" className="border-t border-zinc-900 bg-[#0c0c0c]">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="mb-14 max-w-3xl">
                        <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-4">/ 02 — METHODOLOGIES</div>
                        <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight leading-tight mb-5">
                            Eight methodologies. <span className="text-signal">One right answer</span> per conversation.
                        </h2>
                        <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
                            Most AI writers default to a single generic "friendly" voice. We don't. Every message is written through the lens of a specific, proven methodology — the one you pick. Pick wrong, and you'll close fewer deals. Pick right, and the conversation runs itself.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4" data-testid="methodology-grid">
                        {METHODOLOGIES.map((m) => <MethodologyCard key={m.id} {...m} />)}
                    </div>
                    <div className="mt-10 border border-zinc-900 bg-[#0a0a0a] p-6 flex items-start gap-4">
                        <Brain className="w-6 h-6 text-signal flex-shrink-0 mt-1" strokeWidth={1.5} />
                        <div>
                            <div className="text-xs font-mono uppercase tracking-widest text-signal mb-1">How to pick</div>
                            <p className="text-sm text-zinc-300 leading-relaxed">
                                Not sure which to use? Coach Mode analyses your last 5 conversations and recommends the methodology your prospects respond to best. Jeremy Miner's <span className="text-white">NEPQ</span> is our default — it works in cold DMs because it feels like curiosity, not a pitch.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="how" className="border-t border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="mb-14 max-w-3xl">
                        <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-4">/ 03 — FLOW</div>
                        <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-5">
                            Paste. Pick. Ship.
                        </h2>
                        <p className="text-zinc-400 text-base md:text-lg">Under 8 seconds from blank to booked-meeting-ready DM.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            { step: "01", title: "Paste or start fresh", body: "Drop in a live LinkedIn/Instagram thread — or start a cold outreach from scratch. Upload a product doc if you want the AI to reference specifics." },
                            { step: "02", title: "Pick tone, industry, method", body: "5 tones × 11 industries × 8 methodologies = 440 distinct voices. Plus a goal: book, qualify, or nurture. The prompt writes itself." },
                            { step: "03", title: "Ship the best variant", body: "Three human-grade replies with confidence scores and day-4 follow-ups. Copy, send, move on. Log returns in history." },
                        ].map((s) => (
                            <div key={s.step} className="border border-zinc-900 p-8 bg-[#0c0c0c]">
                                <div className="font-mono text-signal text-sm mb-4">{s.step}</div>
                                <h3 className="font-heading font-bold text-2xl mb-3">{s.title}</h3>
                                <p className="text-zinc-400 text-sm leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="border-t border-zinc-900 bg-[#0c0c0c]">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="mb-14 max-w-3xl">
                        <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-4">/ 04 — PRICING</div>
                        <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-5">
                            Four plans. No surprises. <span className="text-signal">Cancel anytime.</span>
                        </h2>
                        <p className="text-zinc-400 text-base md:text-lg">Start free. Upgrade when you start booking more meetings than you can handle.</p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pricing-grid">
                        {PLANS.map((p) => (
                            <div
                                key={p.id}
                                data-testid={`pricing-card-${p.id}`}
                                className={`relative border p-7 flex flex-col transition-colors ${
                                    p.highlight
                                        ? "border-signal bg-signal/5"
                                        : "border-zinc-900 bg-[#0a0a0a] hover:border-zinc-700"
                                }`}
                            >
                                {p.highlight && (
                                    <div className="absolute -top-3 left-7 px-2 py-0.5 bg-signal text-white text-[10px] font-mono uppercase tracking-widest rounded">
                                        Most Popular
                                    </div>
                                )}
                                <h3 className="font-heading font-black text-2xl mb-1">{p.name}</h3>
                                <p className="text-sm text-zinc-400 mb-5">{p.tagline}</p>

                                <div className="flex items-baseline gap-1 mb-1">
                                    <span className="font-heading font-black text-4xl" data-testid={`pricing-price-${p.id}`}>{p.price}</span>
                                    {p.unit && <span className="text-xs text-zinc-500 font-mono">{p.unit}</span>}
                                </div>
                                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-5 space-y-1">
                                    <div>{p.cap}</div>
                                    <div className="text-zinc-600">{p.users}</div>
                                </div>

                                <ul className="space-y-2 text-sm text-zinc-300 mb-5 flex-1">
                                    {p.features.map((f, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <Check className="w-4 h-4 text-signal mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {p.footnote && (
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-signal/80 mb-4">
                                        {p.footnote}
                                    </div>
                                )}

                                {p.external ? (
                                    <a
                                        href={p.ctaTo}
                                        data-testid={`pricing-cta-${p.id}`}
                                        className="w-full text-center border border-zinc-700 hover:border-signal hover:bg-signal/10 text-white px-4 py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                                    >
                                        <Mail className="w-4 h-4" /> {p.ctaLabel}
                                    </a>
                                ) : (
                                    <Link
                                        to={p.ctaTo}
                                        data-testid={`pricing-cta-${p.id}`}
                                        className={`w-full text-center px-4 py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                                            p.highlight
                                                ? "bg-signal hover:bg-signal-hover text-white"
                                                : "border border-zinc-700 hover:border-signal hover:bg-signal/10 text-white"
                                        }`}
                                    >
                                        {p.ctaLabel}
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 grid md:grid-cols-3 gap-4 text-xs text-zinc-500">
                        <div className="border border-zinc-900 bg-[#0a0a0a] p-4 flex items-start gap-3">
                            <Lock className="w-4 h-4 text-signal mt-0.5" strokeWidth={1.5} />
                            <span>All plans: encrypted at rest & in transit. No model training on your data.</span>
                        </div>
                        <div className="border border-zinc-900 bg-[#0a0a0a] p-4 flex items-start gap-3">
                            <Scale className="w-4 h-4 text-signal mt-0.5" strokeWidth={1.5} />
                            <span>Pro-rated upgrades · downgrade any time · 7-day refund on first charge.</span>
                        </div>
                        <div className="border border-zinc-900 bg-[#0a0a0a] p-4 flex items-start gap-3">
                            <InfinityIcon className="w-4 h-4 text-signal mt-0.5" strokeWidth={1.5} />
                            <span>Pro & Enterprise: generations are unlimited, fair-use enforced at 1,000/day/user.</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Extension */}
            <section id="extension" className="border-t border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-4">/ 05 — EXTENSION</div>
                        <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6">
                            Right inside your DMs.
                        </h2>
                        <p className="text-zinc-300 text-lg leading-relaxed mb-4">
                            Our Chrome extension lives on LinkedIn and Instagram. Pop it open in any conversation, pick your tone + methodology, and get three replies in under 4 seconds — without switching tabs.
                        </p>
                        <ul className="space-y-2 text-sm text-zinc-400 mb-8">
                            <li className="flex gap-2"><Check className="w-4 h-4 text-signal mt-0.5" strokeWidth={2.5}/> Manifest v3 · works on Chrome, Edge, Brave, Arc</li>
                            <li className="flex gap-2"><Check className="w-4 h-4 text-signal mt-0.5" strokeWidth={2.5}/> Remembers your last tone + industry + methodology</li>
                            <li className="flex gap-2"><Check className="w-4 h-4 text-signal mt-0.5" strokeWidth={2.5}/> Free download for every plan tier</li>
                        </ul>
                        <Link
                            to="/signup"
                            data-testid="extension-cta"
                            className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white px-6 py-3.5 rounded-md font-semibold transition-colors"
                        >
                            <Puzzle className="w-4 h-4" />
                            Create an account to install
                        </Link>
                    </div>
                    <div className="border border-zinc-900 p-8 bg-[#0c0c0c] relative">
                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">
                            linkedin.com/messaging
                        </div>
                        <div className="space-y-3 text-sm">
                            <div className="bg-zinc-900/60 p-3 rounded-md border-l-2 border-zinc-700 max-w-[85%]">
                                "Saw your post about scaling outbound — curious, are you running this in-house or outsourcing?"
                            </div>
                            <div className="bg-signal/10 border border-signal/30 p-3 rounded-md max-w-[85%] ml-auto">
                                <div className="text-[10px] uppercase font-mono text-signal tracking-widest mb-1.5">SalesWhisper · NEPQ · Consultative</div>
                                "Mostly in-house, though we've been stress-testing the handoff between SDR and AE. What made you ask — are you seeing something on your end?"
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ / Final CTA */}
            <section className="border-t border-zinc-900 bg-gradient-to-b from-[#0a0a0a] to-[#120804]">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 md:py-32">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-4">/ FAQ</div>
                            <h3 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-6">Quick answers.</h3>
                            <div className="space-y-5 text-sm">
                                <div>
                                    <div className="text-white font-semibold mb-1">Will my replies sound AI-generated?</div>
                                    <p className="text-zinc-400">No. Every message is written through a specific methodology (NEPQ, Challenger, etc.) and capped at under 60 words. That's why they feel like a human closer wrote them — because the frameworks did.</p>
                                </div>
                                <div>
                                    <div className="text-white font-semibold mb-1">Can I upload my product information?</div>
                                    <p className="text-zinc-400">Yes. From Standard onwards, drop PDFs, DOCX, or TXT (up to 10 MB). We extract the text and feed it as context to every outreach message and coaching session.</p>
                                </div>
                                <div>
                                    <div className="text-white font-semibold mb-1">What happens if I hit the monthly generation cap?</div>
                                    <p className="text-zinc-400">You can still read past history and use the objection/follow-up library. New generations resume on the 1st of next month or when you upgrade.</p>
                                </div>
                                <div>
                                    <div className="text-white font-semibold mb-1">Do you train on my data?</div>
                                    <p className="text-zinc-400">Never. We use Claude Sonnet 4.5 via a zero-retention API path. Your conversations stay yours.</p>
                                </div>
                            </div>
                        </div>

                        <div className="text-center md:text-left">
                            <div className="inline-flex items-center gap-2 mb-6">
                                <Sparkles className="w-6 h-6 text-signal" />
                                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Ready when you are</span>
                            </div>
                            <h2 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
                                Start closing <span className="text-signal">more.</span>
                            </h2>
                            <p className="text-zinc-400 text-lg mb-8 max-w-md">
                                Your next booked meeting is one reply away. Free plan. No card required.
                            </p>
                            <Link
                                to="/signup"
                                data-testid="final-cta-signup"
                                className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white px-8 py-4 rounded-md font-semibold text-lg transition-colors group"
                            >
                                Create your account
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="border-t border-zinc-900 py-10">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                    <div className="flex items-center gap-2">
                        <Flame className="w-3.5 h-3.5 text-signal" />
                        saleswhisper — built for closers
                    </div>
                    <div className="flex items-center gap-6">
                        <a href="#features" className="hover:text-white transition-colors">Features</a>
                        <a href="#methodologies" className="hover:text-white transition-colors">Methodologies</a>
                        <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
                        <a href="mailto:sales@saleswhisper.dev" className="hover:text-white transition-colors">Contact</a>
                    </div>
                    <div>© 2026 — never salesy</div>
                </div>
            </footer>
        </div>
    );
}
